import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Col, message, Row, Typography} from 'antd';
import ENDPOINTS from '../../constants/endpoints';
import {ROUTES, formatPath} from '../../constants/PathRoutes';
import api from '../../services/api';
import useDefaultRequest from '../../hooks/useDefaultRequest';
import Form from '../../components/data_entry/form/Form';
import InputFormField from '../../components/data_entry/form/InputFormField';
import TextAreaFormField from '../../components/data_entry/form/TextAreaFormField';
import SwitchFormField from '../../components/data_entry/form/SwitchFormField';
import InputNumberFormField from '../../components/data_entry/form/InputNumberFormField';
import Button from '../../components/data_entry/Button';
import Loading from '../../components/admin/Loading';
import {BuildFieldErrorsDict} from '../../utils/utils';

const FORM_NAMES = {
    NAME: 'name',
    DESCRIPTION: 'description',
    IS_ACTIVE: 'is_active',
    ORDERING: 'ordering',
};

export default function AdminCategoryForm() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const {id} = useParams();
    const isEditing = !!id;

    const {data, isLoading: fetching} = useDefaultRequest({
        url: formatPath(ENDPOINTS.ECOMMERCE_CATEGORY_DETAIL, {id}),
        makeRequest: isEditing,
        redirectRoute: ROUTES.ADMIN_ECOMMERCE_CATEGORY_LIST
    });

    useEffect(() => {
        if (data && isEditing) {
            form.setFieldsValue(data);
        }
    }, [data, isEditing, form]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const payload = {
                [FORM_NAMES.NAME]: values[FORM_NAMES.NAME],
                [FORM_NAMES.DESCRIPTION]: values[FORM_NAMES.DESCRIPTION] || '',
                [FORM_NAMES.IS_ACTIVE]: values[FORM_NAMES.IS_ACTIVE] ?? true,
                [FORM_NAMES.ORDERING]: values[FORM_NAMES.ORDERING] ?? 0,
            };

            if (isEditing) {
                await api.patch(formatPath(ENDPOINTS.ECOMMERCE_CATEGORY_DETAIL, {id}), payload);
                message.success('Categoria atualizada com sucesso');
            } else {
                await api.post(ENDPOINTS.ECOMMERCE_CATEGORY_LIST, payload);
                message.success('Categoria criada com sucesso');
            }
            navigate(ROUTES.ADMIN_ECOMMERCE_CATEGORY_LIST);
        } catch (error) {
            BuildFieldErrorsDict(error, form.setFields);
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return <Loading />;
    }

    return (
        <Row>
            <Col xs={24}>
                <Typography.Title style={{marginTop: 0}} level={2}>
                    {isEditing ? 'Editar Categoria' : 'Nova Categoria'}
                </Typography.Title>
            </Col>
            <Col xs={24}>
                <Form
                    form={form}
                    onFinish={onFinish}
                    initialValues={{
                        [FORM_NAMES.IS_ACTIVE]: true,
                        [FORM_NAMES.ORDERING]: 0,
                    }}
                >
                    <Row gutter={[16, 0]}>
                        <Col xs={24}>
                            <SwitchFormField
                                form={{
                                    label: 'Ativo',
                                    name: FORM_NAMES.IS_ACTIVE,
                                }}
                            />
                        </Col>
                        <Col xs={24} lg={16}>
                            <InputFormField
                                placeholder="Digite o nome da categoria"
                                form={{
                                    label: 'Nome',
                                    name: FORM_NAMES.NAME,
                                    rules: [{
                                        required: true,
                                        message: 'Informe o nome'
                                    }]
                                }}
                            />
                        </Col>
                        <Col xs={24} lg={8}>
                            <InputNumberFormField
                                placeholder="0"
                                min={0}
                                form={{
                                    label: 'Ordenação',
                                    name: FORM_NAMES.ORDERING,
                                    tooltip: 'Controla a ordem de exibição das categorias.',
                                }}
                            />
                        </Col>
                        <Col xs={24}>
                            <TextAreaFormField
                                rows={3}
                                placeholder="Descrição da categoria"
                                form={{
                                    label: 'Descrição',
                                    name: FORM_NAMES.DESCRIPTION,
                                }}
                            />
                        </Col>

                        <Col xs={24} style={{textAlign: 'right', marginTop: 24}}>
                            <Button
                                type="default"
                                style={{marginRight: 8}}
                                onClick={() => navigate(ROUTES.ADMIN_ECOMMERCE_CATEGORY_LIST)}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={form.submit}
                                loading={loading}
                            >
                                Salvar
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Col>
        </Row>
    );
}
